import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import {
  BOOKS,
  SHOP_ITEMS,
  GIFT_CARDS,
  GIFT_CARD_URL,
  LIMITED_EDITIONS_URL,
  getAllPieces,
} from "../data/site";

const SECTIONS = [
  { id: "prints", label: "Prints" },
  { id: "books", label: "Books" },
  { id: "merch", label: "Black Cowboys Exist" },
  { id: "gift-cards", label: "Gift Cards" },
];

export default function Shop() {
  return (
    <div data-testid="shop-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">The Studio Shop</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Shop.
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
          Prints, books, Black Cowboys Exist merch, and gift cards — one clean
          place. Originals live on their own page.
        </p>
        <nav className="mt-10 flex flex-wrap gap-x-6 gap-y-2" data-testid="shop-section-nav">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="label link-underline text-[hsl(var(--ink-soft))] hover:text-foreground"
              data-testid={`shop-jump-${s.id}`}
            >
              {s.label}
            </a>
          ))}
          <Link
            to="/originals"
            className="label link-underline text-foreground"
            data-testid="shop-jump-originals"
          >
            Originals →
          </Link>
        </nav>
      </header>

      <PrintsSection />
      <BooksSection />
      <MerchSection />
      <GiftCardsSection />
    </div>
  );
}

function SectionHead({ eyebrow, title, italic, right }) {
  return (
    <header className="mb-10 md:mb-14 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
      <div className="md:col-span-7">
        <p className="label mb-3">{eyebrow}</p>
        <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
          {title}{" "}
          {italic && <span className="italic text-[hsl(var(--ink-soft))]">{italic}</span>}
        </h2>
      </div>
      {right && <div className="md:col-span-5 md:text-right">{right}</div>}
    </header>
  );
}

function PrintsSection() {
  const picks = getAllPieces()
    .filter(({ meta }) => meta.prints_available || meta.prints_coming_soon)
    .sort((a, b) => (a.meta.prints_available ? 0 : 1) - (b.meta.prints_available ? 0 : 1))
    .slice(0, 4);

  return (
    <section id="prints" className="border-t border-border scroll-mt-28" data-testid="shop-prints">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <SectionHead
          eyebrow="01 · Prints"
          title="Collector editions and"
          italic="open editions."
          right={
            <Link
              to="/prints"
              className="inline-flex items-center gap-2 label link-underline"
              data-testid="shop-prints-all"
            >
              Browse all prints <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border mb-12">
          <Link
            to="/prints#collector-editions"
            className="bg-background p-8 md:p-10 hover:bg-[hsl(var(--surface))] transition-colors group"
            data-testid="shop-tier-collector"
          >
            <p className="label">Collector Editions</p>
            <h3 className="serif text-3xl md:text-4xl mt-3 group-hover:italic transition-all">
              Signed · Numbered · Archival
            </h3>
            <p className="serif text-2xl mt-4">$150–$500+</p>
            <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed max-w-md">
              Hand-signed by the artist, numbered, printed on museum-grade
              cotton paper and shipped flat from the Denver studio.
            </p>
          </Link>
          <Link
            to="/prints#open-editions"
            className="bg-background p-8 md:p-10 hover:bg-[hsl(var(--surface))] transition-colors group"
            data-testid="shop-tier-open"
          >
            <p className="label">Open Editions</p>
            <h3 className="serif text-3xl md:text-4xl mt-3 group-hover:italic transition-all">
              Accessible · Multiple sizes
            </h3>
            <p className="serif text-2xl mt-4">$45–$195</p>
            <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed max-w-md">
              Fulfilled automatically by Gelato. Coming soon — join the list
              to be first.
            </p>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {picks.map(({ piece, meta, seriesSlug }) => (
            <Link
              key={piece.id}
              to={`/work/${seriesSlug}/${piece.id}`}
              className="art-card block"
              data-testid={`shop-print-pick-${piece.id}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
                <img src={piece.image} alt={piece.title} className="art-img w-full h-full object-cover" />
                <span className="absolute top-3 left-3 inline-block bg-background text-foreground border border-foreground px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
                  {meta.prints_available ? "Prints Available" : "Coming Soon"}
                </span>
              </div>
              <p className="serif italic text-lg mt-3">{piece.title}</p>
            </Link>
          ))}
        </div>
        <a
          href={LIMITED_EDITIONS_URL}
          target="_blank"
          rel="noreferrer"
          className="mt-10 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
          data-testid="shop-limited-editions-square"
        >
          Shop signed editions on Square
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
}

function BooksSection() {
  return (
    <section id="books" className="border-t border-border bg-[hsl(var(--surface))] scroll-mt-28" data-testid="shop-books">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <SectionHead
          eyebrow="02 · Books"
          title="Written & illustrated"
          italic="by Joseph Graves Jr."
          right={
            <Link to="/books" className="inline-flex items-center gap-2 label link-underline" data-testid="shop-books-all">
              About the books <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {BOOKS.map((b) => (
            <a
              key={b.id}
              href={b.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group grid grid-cols-[120px_1fr] md:grid-cols-[180px_1fr] gap-6 md:gap-8 items-start"
              data-testid={`shop-book-${b.id}`}
            >
              <div className="aspect-[3/4] overflow-hidden bg-background shadow-sm">
                <img src={b.cover} alt={b.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
              </div>
              <div>
                <p className="label">{b.badge} · {b.format}</p>
                <h3 className="serif text-2xl md:text-3xl mt-2 leading-tight group-hover:italic transition-all">{b.title}</h3>
                <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{b.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-2 label">
                  Buy the book <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function MerchSection() {
  const items = SHOP_ITEMS.filter((s) => s.group === "Black Cowboys Exist");
  return (
    <section id="merch" className="border-t border-[hsl(var(--rule))] bg-foreground text-background scroll-mt-28" data-testid="shop-merch">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-[1.1fr_2fr] gap-10 md:gap-20 items-end mb-10 md:mb-14">
          <div>
            <p className="label mb-3 text-background/60">03 · Merch</p>
            <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Black Cowboys <em className="italic">Exist</em>.
            </h2>
          </div>
          <p className="text-background/75 max-w-prose text-base md:text-lg leading-relaxed">
            Hats, tees, patches, and stickers cut in the same colors as the
            paintings. Fulfilled by Graves Collective, ships worldwide.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-background/15">
          {items.map((s, idx) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-foreground p-6 md:p-8 min-h-[180px] md:min-h-[220px] flex flex-col justify-between hover:bg-[hsl(var(--accent))] transition-colors duration-500"
              data-testid={`shop-merch-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="label text-background/50 group-hover:text-background/80">
                No. {String(idx + 1).padStart(2, "0")}
              </span>
              <div>
                <p className="serif text-2xl md:text-3xl leading-[1.05] tracking-tight">{s.label}</p>
                <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-background/70 group-hover:text-background">
                  <span>Shop the collective</span>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function GiftCardsSection() {
  return (
    <section id="gift-cards" className="border-t border-border scroll-mt-28" data-testid="shop-gift-cards">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <SectionHead
          eyebrow="04 · Gift Cards"
          title="Give the studio."
          italic="Let them pick the piece."
          right={
            <p className="text-sm text-[hsl(var(--ink-soft))] md:text-right">
              Works across prints, books, and apparel. Delivered by email,
              redeemed in a click.
            </p>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {GIFT_CARDS.map((g) => (
            <a
              key={g.amount}
              href={GIFT_CARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
              data-testid={`gift-card-${g.amount.replace("$", "")}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[hsl(var(--surface))] flex items-center justify-center p-6">
                <img
                  src={g.image}
                  alt={`Graves Collective ${g.amount} gift card`}
                  className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                />
              </div>
              <div className="mt-5 flex items-start justify-between gap-6">
                <div>
                  <p className="label mb-1">Gift Card</p>
                  <h3 className="serif text-2xl md:text-3xl tracking-tight font-light">{g.amount}</h3>
                  <p className="mt-1 text-sm text-[hsl(var(--ink-soft))] max-w-md">{g.blurb}</p>
                </div>
                <ArrowUpRight className="h-5 w-5 mt-2 text-[hsl(var(--ink-soft))] shrink-0" />
              </div>
            </a>
          ))}
        </div>
        <p className="mt-10 text-sm text-[hsl(var(--ink-soft))]">
          Other amounts available at{" "}
          <a href={GIFT_CARD_URL} target="_blank" rel="noopener noreferrer" className="link-underline text-foreground" data-testid="gift-cards-shop-link">
            Graves Collective →
          </a>
        </p>
      </div>
    </section>
  );
}
